import { useState } from "react"
import Dialog from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Select from "@/v2-ui/select"
import Button from "@/v2-backoffice-client/components/button"
import { useTenantActivationResendMutation } from "@/v2-backoffice-client/tenant/migration/tenant.migration.resend.mutation"
import {
  ACTIVATION_STATUS_NOT_MIGRATED,
  getActivationOutcomeStyle,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import type { TenantActivationResendMutation } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.resend.mutation"

type ResendOutcome = TenantActivationResendMutation["resendUserActivation"]

export type UserMigrateDialogProps = {
  user: UserFragment
  /**
   * The tenant the send should attribute to (activations are tenant-scoped).
   * When omitted, resolved from the user's memberships: a single membership
   * resolves silently, several show a picker.
   */
  tenantId?: number
  /** "Send activation" or "Resend activation" — derived by the cell. */
  actionLabel: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Confirm-and-run dialog for migrating ONE user to Fusion login (FUS-927):
 * runs the same per-user routine the tenant wave runs
 * (`resendUserActivation`), so individual sends and waves can never drift.
 * The mutation selects the user's post-action row, which Apollo merges into
 * the normalized `User:<Id>` cache entry — every open table and panel flips
 * the badge without a refetch.
 */
function UserMigrateDialog(props: UserMigrateDialogProps) {
  const { user, tenantId, actionLabel, open, onOpenChange } = props

  const memberships = user.Tenants ?? []
  const [pickedTenantId, setPickedTenantId] = useState<number | undefined>(
    tenantId ?? (memberships.length === 1 ? memberships[0]?.Id : undefined),
  )
  const [outcome, setOutcome] = useState<ResendOutcome | null>(null)

  const [resend, { loading, error, reset }] =
    useTenantActivationResendMutation()

  const isFirstSend = user.MigrationStatus === ACTIVATION_STATUS_NOT_MIGRATED

  function handleOpenChange(next: boolean) {
    if (!next) {
      setOutcome(null)
      reset()
    }
    onOpenChange(next)
  }

  async function submit() {
    if (!pickedTenantId) return
    const res = await resend({
      variables: { tenantId: pickedTenantId, userId: user.Id },
    })
    if (res.data?.resendUserActivation) {
      setOutcome(res.data.resendUserActivation)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content align="top">
        {!outcome ? (
          <>
            <Dialog.Header>
              <Dialog.Title>{actionLabel}</Dialog.Title>
              <Dialog.Description>
                {isFirstSend
                  ? "Creates a Fusion login account for this user and emails them a personal activation link (valid 30 days) to set their own password. They keep signing in the old way until they activate — nobody is locked out."
                  : "Renews this user's activation and re-sends the email with a fresh personal link (valid 30 days). The previously emailed link stops working."}
              </Dialog.Description>
            </Dialog.Header>

            <ErrorMessage className="my-4 text-center" error={error} />

            <div className="space-y-2 py-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {user.FirstName} {user.LastName}
                </span>
                <span className="text-muted-foreground">{user.Email}</span>
              </div>
              {memberships.length > 1 && (
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  Attribute to team
                  <Select
                    value={pickedTenantId ? String(pickedTenantId) : undefined}
                    onValueChange={(value) => setPickedTenantId(Number(value))}
                  >
                    <Select.Trigger>
                      <Select.Value placeholder="Choose team…" />
                    </Select.Trigger>
                    <Select.Content>
                      {memberships.map((tenant) => (
                        <Select.Item key={tenant.Id} value={String(tenant.Id)}>
                          {tenant.Name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </label>
              )}
            </div>

            <Dialog.Footer>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                isLoading={loading}
                disabled={!pickedTenantId}
                onClick={() => void submit()}
              >
                {actionLabel}
              </Button>
            </Dialog.Footer>
          </>
        ) : (
          <>
            <Dialog.Header>
              <Dialog.Title>
                {getActivationOutcomeStyle(outcome.Outcome).label}
              </Dialog.Title>
              <Dialog.Description>
                {outcome.Error ??
                  (outcome.Email
                    ? `The activation email went to ${outcome.Email}. The status board tracks them from here.`
                    : "Done. The status board tracks them from here.")}
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Footer>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default UserMigrateDialog
